import { Component, EventEmitter, Input, Output, OnChanges, OnDestroy, SimpleChanges } from "@angular/core";
import { CommonModule, NgStyle } from "@angular/common";
import { Router, RouterModule, NavigationEnd } from "@angular/router";
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  LucideAngularModule,
  Menu,
  X,
  Bot,
  Sun,
  Moon,
  User,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  LogOut,
  EllipsisVertical
} from "lucide-angular";
import { BrandLogoComponent } from "../../brand-logo/brand-logo.component";
import { UserModel } from "../../../../core/models/user";
import { EnvironmentIndicatorComponent } from "../../environment-indicator/environment-indicator.component";
import { AuthService } from "../../../../core/services/auth.service";
import { ButtonDisplayComponent, ButtonDisplayItem } from "../../button-display/button-display.component";
import { MatTooltipModule } from '@angular/material/tooltip';

interface NavigationItem {
  id: string;
  label: string;
  icon: any;
  children?: NavigationItem[];
  isExpanded?: boolean;
  requiresAuth?: boolean;
}

@Component({
  selector: "app-sidebar",
  standalone: true,
  imports: [LucideAngularModule, MatTooltipModule, RouterModule, NgStyle, CommonModule, BrandLogoComponent, EnvironmentIndicatorComponent, ButtonDisplayComponent],
  templateUrl: "./sidebar.component.html",
})
export class SidebarComponent implements OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  // Icons
  readonly Menu = Menu;
  readonly X = X;
  readonly Bot = Bot;
  readonly Sun = Sun;
  readonly Moon = Moon;
  readonly User = User;
  readonly ChevronDown = ChevronDown;
  readonly ChevronRight = ChevronRight;
  readonly ChevronLeft = ChevronLeft;
  readonly LogOut = LogOut;
  readonly EllipsisVertical = EllipsisVertical;
  @Input() isMobileMenuOpen: boolean = false;
  @Input() publicNavigationItems: NavigationItem[] = [];
  @Input() rbacNavigationItems: NavigationItem[] = [];
  @Input() user: UserModel | null = null;
  @Input() isDarkTheme: boolean = false;

  @Output() toggleMobileMenu = new EventEmitter<void>();
  @Output() toggleTheme = new EventEmitter<void>();
  @Output() navigate = new EventEmitter<string>();

  // Sidebar collapse state
  isSidebarCollapsed: boolean = false;
  isMobile: boolean = false;
  
  // Getter que retorna false en mobile para que siempre se vea expandido
  get isSidebarCollapsedEffective(): boolean {
    return this.isMobile ? false : this.isSidebarCollapsed;
  }
  
  // User menu state
  isUserMenuOpen: boolean = false;

  // Menu items configuration
  expandedMenuItems: ButtonDisplayItem[] = [];
  compactMenuItems: ButtonDisplayItem[] = [];

  private readonly SIDEBAR_COLLAPSE_KEY = 'sidebar-collapsed';

  private allNavigationItems: NavigationItem[] = [];

  constructor(public router: Router, private readonly authService: AuthService) {
    // Leer el estado colapsado del localStorage
    const stored = localStorage.getItem(this.SIDEBAR_COLLAPSE_KEY);
    this.isSidebarCollapsed = stored === 'true';

    // Detectar si estamos en mobile (< 768px = md breakpoint)
    this.checkIsMobile();
    window.addEventListener('resize', () => this.checkIsMobile());

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.autoExpandMenu();
      });
    
    this.initializeMenuItems();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkIsMobile(): void {
    this.isMobile = window.innerWidth < 768; // md breakpoint de Tailwind
  }

  ngOnChanges(changes: SimpleChanges) {
    // Cuando llegan los navigationItems (o cambian), recalcular la lista plana
    if (
      (changes['publicNavigationItems'] && this.publicNavigationItems.length > 0) ||
      (changes['rbacNavigationItems'] && this.rbacNavigationItems.length > 0)
    ) {
      this.allNavigationItems = this.getAllNavigationItems();
    }
    if (changes['rbacNavigationItems'] && this.rbacNavigationItems.length > 0) {
      this.autoExpandMenu();
    }
    if (changes['isDarkTheme']) {
      this.initializeMenuItems();
    }
  }

  private initializeMenuItems(): void {
    // Menu items para modo expandido
    this.expandedMenuItems = [
      {
        id: 'theme-toggle',
        iconCondition: this.isDarkTheme,
        icon: Sun,
        alternativeIcon: Moon,
        label: this.isDarkTheme ? 'Modo claro' : 'Modo oscuro',
        action: () => this.toggleTheme.emit(),
        showSeparatorAfter: true
      },
      {
        id: 'logout',
        icon: LogOut,
        label: 'Cerrar sesión',
        action: () => this.onLogout(),
        type: 'text-danger'
      }
    ];

    // Menu items para modo compacto (solo íconos)
    this.compactMenuItems = [
      {
        id: 'theme-toggle',
        iconCondition: this.isDarkTheme,
        icon: Sun,
        alternativeIcon: Moon,
        ariaLabel: this.isDarkTheme ? 'Modo claro' : 'Modo oscuro',
        action: () => this.toggleTheme.emit(),
      },
      {
        id: 'logout',
        icon: LogOut,
        ariaLabel: 'Cerrar sesión',
        action: () => this.onLogout(),
        type: 'text-danger'
      }
    ];
  }

  getUserFullName(): string {
    return this.user?.name.split(" ")[0] + " " + this.user?.lastName.split(" ")[0] || "Usuario";
  }

  onNavigate(path: string): void {
    this.navigate.emit(path);
  }

  toggleSubMenu(item: NavigationItem): void {
    if (item.children) {
      item.isExpanded = !item.isExpanded;
    }
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    localStorage.setItem(this.SIDEBAR_COLLAPSE_KEY, String(this.isSidebarCollapsed));
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  closeUserMenu(): void {
    this.isUserMenuOpen = false;
  }

  autoExpandMenu() {
    for (const item of this.rbacNavigationItems) {
      if (item.children) {
        // Verificar si algún hijo coincide con la ruta actual
        const shouldExpand = item.children.some(child => 
          this.router.url.startsWith('/' + child.id)
        );
        
        if (shouldExpand && !item.isExpanded) {
          item.isExpanded = true;
        }
        // No colapsar automáticamente para no interferir con clicks manuales
      }
    }
  }


  isRouteActive(itemId: string): boolean {
    const currentPath = this.router.url.split('?')[0]; // Remover query params
    const itemPath = '/' + itemId;
    // Debe coincidir exactamente o como prefijo con /
    if (currentPath === itemPath || currentPath.startsWith(itemPath + '/')) {
      // Verificar que no haya otro item con una ruta más específica que también coincida
      const longerMatch = this.allNavigationItems.find(item =>
        item.id !== itemId &&
        item.id.startsWith(itemId) &&
        (currentPath === '/' + item.id || currentPath.startsWith('/' + item.id + '/'))
      );
      return !longerMatch;
    }
    return false;
  }

  private getAllNavigationItems(): NavigationItem[] {
    const items: NavigationItem[] = [...this.publicNavigationItems, ...this.rbacNavigationItems];
    const allItems: NavigationItem[] = [];
    for (const item of items) {
      allItems.push(item);
      if (item.children) {
        allItems.push(...item.children);
      }
    }
    return allItems;
  }


  onLogout(): void {
    this.authService.logout();
  }
}
